import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import axios from 'axios';
import styles from './DipGallery.module.css';
import DipGalleryChart from './DipGalleryChart';
import DipGalleryCard from './DipGalleryCard';
import { API } from '../../config/api';
import { IoArrowBack, IoChevronBack, IoChevronForward } from 'react-icons/io5';

// Simple in-memory cache for API responses
const chartDataCache = new Map();

const cacheManager = {
  get: (key) => chartDataCache.get(key),
  set: (key, data, ttl = 5 * 60 * 1000) => {
    chartDataCache.set(key, data);
    setTimeout(() => {
      chartDataCache.delete(key);
    }, ttl);
  },
  has: (key) => chartDataCache.has(key),
};

const DipGallery = ({ stocks, onGalleryModeChange }) => {
  const [isGalleryMode, setIsGalleryMode] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Refs for scrolling to selected card
  const stripContainerRef = useRef(null);
  const cardRefs = useRef([]);

  // Notify parent when gallery mode changes
  React.useEffect(() => {
    onGalleryModeChange?.(isGalleryMode);
  }, [isGalleryMode, onGalleryModeChange]);

  // Get selected stock data
  const selectedStock = useMemo(() => {
    if (selectedIndex === null || !stocks) return null;
    return stocks[selectedIndex];
  }, [selectedIndex, stocks]);

  // Generate logo source for a stock
  const getLogoSrc = useCallback((stockSymbol) => {
    try {
      return require(`../../assets/image/stock-logo/${stockSymbol}.png`);
    } catch (err) {
      return require('../../assets/image/stock-logo/default.png');
    }
  }, []);

  // Fetch chart data for a stock
  const fetchChartData = useCallback(async (stock, index) => {
    setLoading(true);
    setError(null);
    setSelectedIndex(index);
    setIsGalleryMode(true);

    const cacheKey = `${stock.stockUid}-${stock.month_range}`;

    try {
      if (cacheManager.has(cacheKey)) {
        const cachedData = cacheManager.get(cacheKey);
        setChartData(cachedData);
        return;
      }

      const response = await axios.get(API.getChartDips, {
        params: {
          stockUid: stock.stockUid,
          months: stock.month_range
        }
      });

      const { data: chartApiData } = response.data;
      cacheManager.set(cacheKey, chartApiData);
      setChartData(chartApiData);
    } catch (err) {
      console.error('Error fetching chart data:', err);
      setError('Failed to load chart data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle card click in gallery mode (switch to different stock)
  const handleCardSelect = useCallback((stock, index) => {
    if (index === selectedIndex) return;
    fetchChartData(stock, index);
  }, [selectedIndex, fetchChartData]);

  // Exit gallery mode
  const handleExitGallery = useCallback(() => {
    setIsGalleryMode(false);
    setSelectedIndex(null);
    setChartData(null);
    setError(null);
  }, []);

  // Navigate to previous stock
  const handleNavigatePrev = useCallback(() => {
    if (!stocks || selectedIndex === null || selectedIndex <= 0) return;
    handleCardSelect(stocks[selectedIndex - 1], selectedIndex - 1);
  }, [stocks, selectedIndex, handleCardSelect]);

  // Navigate to next stock
  const handleNavigateNext = useCallback(() => {
    if (!stocks || selectedIndex === null || selectedIndex >= stocks.length - 1) return;
    handleCardSelect(stocks[selectedIndex + 1], selectedIndex + 1);
  }, [stocks, selectedIndex, handleCardSelect]);

  // Check if navigation is possible
  const canNavigatePrev = selectedIndex !== null && selectedIndex > 0;
  const canNavigateNext = selectedIndex !== null && stocks && selectedIndex < stocks.length - 1;

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isGalleryMode || !stocks) return;

    if (e.key === 'Escape') {
      handleExitGallery();
    } else if (e.key === 'ArrowLeft' && selectedIndex > 0) {
      handleCardSelect(stocks[selectedIndex - 1], selectedIndex - 1);
    } else if (e.key === 'ArrowRight' && selectedIndex < stocks.length - 1) {
      handleCardSelect(stocks[selectedIndex + 1], selectedIndex + 1);
    }
  }, [isGalleryMode, stocks, selectedIndex, handleCardSelect, handleExitGallery]);

  // Attach keyboard listener
  React.useEffect(() => {
    if (isGalleryMode) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isGalleryMode, handleKeyDown]);

  // Scroll to selected card in the horizontal strip
  useEffect(() => {
    if (isGalleryMode && selectedIndex !== null && stripContainerRef.current && cardRefs.current[selectedIndex]) {
      const container = stripContainerRef.current;
      const selectedCard = cardRefs.current[selectedIndex];
      
      // Calculate the scroll position to center the selected card
      const containerWidth = container.offsetWidth;
      const cardLeft = selectedCard.offsetLeft;
      const cardWidth = selectedCard.offsetWidth;
      
      // Scroll to position that centers the card in the container
      const scrollPosition = cardLeft - (containerWidth / 2) + (cardWidth / 2);
      
      container.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: 'smooth'
      });
    }
  }, [isGalleryMode, selectedIndex]);

  // Grid view (default view)
  if (!isGalleryMode) {
    return (
      <div className={styles.gridContainer}>
        <div className="row">
          {stocks?.map((stock, index) => (
            <div 
              key={stock.stockUid || index} 
              className="col-xl-3 col-lg-4 col-md-6 col-sm-12"
            >
              <DipGalleryCard
                data={stock}
                logoSrc={getLogoSrc(stock.stock)}
                isSelected={false}
                isCompact={false}
                onClick={() => fetchChartData(stock, index)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Gallery view (chart + horizontal strip)
  return (
    <div className={styles.galleryContainer}>
      {/* Main Chart Section */}
      <div className={styles.mainSection}>
        <div className={styles.galleryHeader}>
          <button
            className={styles.backButton}
            onClick={handleExitGallery}
            aria-label="Exit gallery mode"
            tabIndex={0}
          >
            <IoArrowBack />
            <span>Back to Grid</span>
          </button>
          
          <div className={styles.navigationControls}>
            <button
              className={`${styles.navArrowButton} ${!canNavigatePrev ? styles.navArrowDisabled : ''}`}
              onClick={handleNavigatePrev}
              disabled={!canNavigatePrev}
              aria-label="Previous stock"
              tabIndex={0}
            >
              <IoChevronBack />
            </button>
            
            <span className={styles.navigationCounter}>
              {selectedIndex !== null ? selectedIndex + 1 : 0} / {stocks?.length || 0}
            </span>
            
            <button
              className={`${styles.navArrowButton} ${!canNavigateNext ? styles.navArrowDisabled : ''}`}
              onClick={handleNavigateNext}
              disabled={!canNavigateNext}
              aria-label="Next stock"
              tabIndex={0}
            >
              <IoChevronForward />
            </button>
          </div>
        </div>

        <div className={styles.chartArea}>
          {loading && (
            <div className={styles.loadingState}>
              <div className={styles.spinner} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p>Loading chart data...</p>
            </div>
          )}

          {error && (
            <div className={styles.errorState}>
              <p>{error}</p>
              <button 
                onClick={() => fetchChartData(selectedStock, selectedIndex)}
                className={styles.retryButton}
              >
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && chartData && (
            <DipGalleryChart
              data={chartData}
              logoSrc={getLogoSrc(selectedStock?.stock)}
            />
          )}
        </div>
      </div>

      {/* Horizontal Card Strip */}
      <div className={styles.stripSection}>
        <div className={styles.stripContainer} ref={stripContainerRef}>
          {stocks?.map((stock, index) => (
            <div 
              key={stock.stockUid || index}
              className={styles.stripCardWrapper}
              ref={el => cardRefs.current[index] = el}
            >
              <DipGalleryCard
                data={stock}
                logoSrc={getLogoSrc(stock.stock)}
                isSelected={index === selectedIndex}
                isCompact={true}
                onClick={() => handleCardSelect(stock, index)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DipGallery;

